import { useEffect, useMemo, useRef, useState } from 'react'
import { isAxiosError } from 'axios'
import { useSearchParams } from 'react-router-dom'
import {
  ChevronRight,
  Contact,
  Folder,
  FolderPlus,
  HardDrive,
  Inbox,
  Pencil,
  Trash2,
  Upload,
  Users,
} from 'lucide-react'
import { Button, ConfirmDialog } from '@/components/ui'
import { AccessDenied } from '@/components/common/AccessDenied'
import { EmptyState } from '@/components/common/EmptyState'
import { QueryErrorState } from '@/components/common/QueryErrorState'
import { SectionLoader } from '@/components/common/SectionLoader'
import { TableRowAction, TableRowActions } from '@/components/common/TableRowActions'
import { WorkspaceToolbarSearch } from '@/components/common/WorkspaceToolbar'
import {
  CANDIDATE_FILES,
  EMPLOYEE_FILES,
  RECRUITMENT_KEY,
  UNFILED,
  type DriveFolder,
} from '@/api/services/drive.service'
import { getCompanyColor } from '@/features/shifts/utils/companyColor'
import { cn } from '@/lib/cn'
import { formatDate } from '@/lib/formatters'
import { useUploadCompanyDocuments } from '../../hooks/useDocuments'
import {
  useCreateDriveFolder,
  useDeleteDriveFolder,
  useDriveCompanySummaries,
  useDriveFolders,
  useRenameDriveFolder,
} from '../../hooks/useDrive'
import {
  DOCUMENT_UPLOAD_ACCEPT,
  formatFileSize,
  getDocumentUploadErrorMessage,
  validateDocumentFiles,
} from '../../utils/documentUtils'
import { childFolders, countLabel, folderPath, folderSummary } from '../../utils/driveTree'
import { DriveFiles } from './DriveFiles'
import { DriveFolderDialog } from './DriveFolderDialog'

interface DriveCompany {
  _id: string
  name: string
  color?: string
}

interface DriveLocation {
  companyId: string
  folderId: string | null
}

interface Crumb {
  label: string
  onSelect: () => void
}

interface DriveHeaderProps {
  /** The way back up, top level first. */
  trail: Crumb[]
  title: string
  summary?: string
  search: React.ReactNode
  actions?: React.ReactNode
}

/** Where you are is the page's heading; the parents sit quietly above it. */
function DriveHeader({ trail, title, summary, search, actions }: DriveHeaderProps): React.ReactElement {
  const trailRef = useRef<HTMLOListElement>(null)
  const trailKey = trail.map((crumb) => crumb.label).join('/')

  // A long trail scrolls sideways on a phone: keep the nearest parent in view.
  useEffect(() => {
    const element = trailRef.current
    if (element) element.scrollLeft = element.scrollWidth
  }, [trailKey])

  return (
    <header className="flex flex-col gap-3 xl:flex-row xl:items-end xl:justify-between xl:gap-6">
      <div className="min-w-0">
        {trail.length > 0 ? (
          <nav aria-label="Location">
            <ol
              ref={trailRef}
              className="mb-1 flex items-center gap-1 overflow-x-auto whitespace-nowrap text-sm text-gray-500 dark:text-gray-400"
            >
              {trail.map((crumb, index) => (
                <li key={`${crumb.label}-${index}`} className="flex items-center gap-1">
                  {index > 0 ? <ChevronRight className="h-3.5 w-3.5 shrink-0" aria-hidden /> : null}
                  <button
                    type="button"
                    onClick={crumb.onSelect}
                    className="rounded transition-colors hover:text-primary"
                  >
                    {crumb.label}
                  </button>
                </li>
              ))}
            </ol>
          </nav>
        ) : null}
        <h2 className="truncate text-lg font-semibold text-gray-900 dark:text-gray-100">{title}</h2>
        {summary ? (
          <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">{summary}</p>
        ) : null}
      </div>
      <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center">
        <div className="min-w-0 flex-1 xl:w-72 xl:flex-none">{search}</div>
        {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
      </div>
    </header>
  )
}

interface BrowserRow {
  key: string
  name: string
  /** Icon, or a company's colour mark. */
  mark: React.ReactNode
  /** Second line on phones, where the columns don't fit. */
  detail: string
  fileCount: string
  size: string
  updated: string
  onOpen: () => void
  actions?: React.ReactNode
}

/**
 * The one list everything is browsed in, companies and folders alike, so each
 * level looks and behaves the same. Files use the document list below it.
 */
function BrowserTable({
  label,
  nameHeading,
  rows,
  showActions,
}: {
  label: string
  nameHeading: string
  rows: BrowserRow[]
  showActions: boolean
}): React.ReactElement {
  return (
    <table className="w-full table-fixed border-collapse text-sm" aria-label={label}>
      <thead>
        <tr className="border-b border-gray-200 text-left text-xs font-medium text-gray-500 dark:border-gray-700 dark:text-gray-400">
          <th scope="col" className="py-2 pr-3 font-medium">
            {nameHeading}
          </th>
          <th scope="col" className="hidden w-24 py-2 pr-3 text-right font-medium sm:table-cell">
            Files
          </th>
          <th scope="col" className="hidden w-24 py-2 pr-3 text-right font-medium md:table-cell">
            Size
          </th>
          <th scope="col" className="hidden w-32 py-2 pr-3 text-right font-medium lg:table-cell">
            Last added
          </th>
          {showActions ? (
            <th scope="col" className="w-[4.5rem] py-2">
              <span className="sr-only">Actions</span>
            </th>
          ) : null}
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
        {rows.map((row) => (
          <tr
            key={row.key}
            onClick={row.onOpen}
            className="group/row cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/60"
          >
            <td className="py-2.5 pr-3">
              <div className="flex min-w-0 items-center gap-3">
                <span className="flex h-5 w-5 shrink-0 items-center justify-center">{row.mark}</span>
                <div className="min-w-0">
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation()
                      row.onOpen()
                    }}
                    className="block max-w-full truncate text-left font-medium text-gray-900 hover:text-primary dark:text-gray-100"
                  >
                    {row.name}
                  </button>
                  <p className="truncate text-xs text-gray-500 dark:text-gray-400 sm:hidden">
                    {row.detail}
                  </p>
                </div>
              </div>
            </td>
            <td className="hidden py-2.5 pr-3 text-right tabular-nums text-gray-600 dark:text-gray-300 sm:table-cell">
              {row.fileCount}
            </td>
            <td className="hidden py-2.5 pr-3 text-right tabular-nums text-gray-600 dark:text-gray-300 md:table-cell">
              {row.size}
            </td>
            <td className="hidden py-2.5 pr-3 text-right tabular-nums text-gray-600 dark:text-gray-300 lg:table-cell">
              {row.updated}
            </td>
            {showActions ? (
              <td className="py-1" onClick={(event) => event.stopPropagation()}>
                {row.actions}
              </td>
            ) : null}
          </tr>
        ))}
      </tbody>
    </table>
  )
}

const NONE = '—'

function sizeLabel(size: number): string {
  return size > 0 ? formatFileSize(size) : NONE
}

function dateLabel(date: string | null | undefined): string {
  return date ? formatDate(date, 'dd MMM yyyy') : NONE
}

const MARK_CLASS = 'h-5 w-5 text-gray-400 dark:text-gray-500'
const folderMark = <Folder className={MARK_CLASS} aria-hidden />

interface DriveCompanyViewProps {
  company: DriveCompany
  /** A folder id, `unfiled`, or null for the top of the company. */
  folderId: string | null
  /** The way back up to the list of companies, when there is more than one. */
  rootCrumb?: Crumb
  canManage: boolean
  canDelete: boolean
  onNavigate: (location: DriveLocation) => void
}

function DriveCompanyView({
  company,
  folderId,
  rootCrumb,
  canManage,
  canDelete,
  onNavigate,
}: DriveCompanyViewProps): React.ReactElement {
  const companyId = company._id
  const { data, isLoading, error, refetch } = useDriveFolders(companyId)
  const createFolder = useCreateDriveFolder(companyId)
  const renameFolder = useRenameDriveFolder(companyId)
  const deleteFolder = useDeleteDriveFolder(companyId)
  const upload = useUploadCompanyDocuments(companyId)

  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [isCreating, setIsCreating] = useState(false)
  const [renaming, setRenaming] = useState<DriveFolder | null>(null)
  const [deleting, setDeleting] = useState<DriveFolder | null>(null)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const id = window.setTimeout(() => setSearch(searchInput.trim()), 300)
    return () => window.clearTimeout(id)
  }, [searchInput])

  const folders = data?.folders ?? []
  const unfiledCount = data?.unfiledCount ?? 0
  const employeeFileCount = data?.employeeFileCount ?? 0
  const candidateFileCount = data?.candidateFileCount ?? 0
  const isUnfiled = folderId === UNFILED
  const isEmployeeFiles = folderId === EMPLOYEE_FILES
  const isCandidateFiles = folderId === CANDIDATE_FILES
  // These aren't folders: they're fixed views, reached from a row.
  const isFixedView = isUnfiled || isEmployeeFiles || isCandidateFiles
  const isReadOnlyView = isEmployeeFiles || isCandidateFiles
  const recruitment = folders.find((folder) => folder.systemKey === RECRUITMENT_KEY)
  const path = folderPath(
    folders,
    isCandidateFiles ? (recruitment?.id ?? null) : isFixedView ? null : folderId,
  )
  // A link to a folder that has since gone falls back to the top of the company.
  const current = isFixedView ? null : (path[path.length - 1] ?? null)
  const canUploadHere = canManage && current !== null
  const canAddFolderHere = canManage && !isFixedView
  const subfolders = isFixedView ? [] : childFolders(folders, current?.id ?? null)

  const uploadFiles = (files: FileList | File[]): void => {
    if (!current) return
    const list = Array.from(files)
    const problem = validateDocumentFiles(list)
    setUploadError(problem)
    if (problem) return
    upload.mutate(
      { files: list, params: { ownerType: 'company', folderId: current.id } },
      { onError: (uploadFailure) => setUploadError(getDocumentUploadErrorMessage(uploadFailure)) },
    )
  }

  if (isAxiosError(error) && error.response?.status === 403) {
    return <AccessDenied resourceName={`the ${company.name} Drive`} />
  }

  const atCompanyTop = !current && !isFixedView
  const trail: Crumb[] = [
    ...(rootCrumb ? [rootCrumb] : []),
    ...(atCompanyTop
      ? []
      : [{ label: company.name, onSelect: () => onNavigate({ companyId, folderId: null }) }]),
    ...(isCandidateFiles ? path : path.slice(0, -1)).map((folder) => ({
      label: folder.name,
      onSelect: () => onNavigate({ companyId, folderId: folder.id }),
    })),
  ]
  const title = isUnfiled
    ? 'Unfiled'
    : isEmployeeFiles
      ? 'Employee files'
      : isCandidateFiles
        ? 'Candidate files'
        : (current?.name ?? company.name)

  const folderTotal =
    folders.reduce((total, folder) => total + folder.fileCount, 0) +
    unfiledCount +
    employeeFileCount +
    candidateFileCount
  const summary = !data
    ? undefined
    : atCompanyTop
      ? `${countLabel(subfolders.length, 'folder')} · ${countLabel(folderTotal, 'file')}`
      : isUnfiled
        ? `${countLabel(unfiledCount, 'file')} not in a folder yet`
        : isEmployeeFiles
          ? `${countLabel(employeeFileCount, 'file')} · Read-only here. Manage them in Staff documents.`
          : isCandidateFiles
            ? `${countLabel(candidateFileCount, 'file')} · Read-only here. Manage them in Candidates.`
            : current
          ? folderSummary(folders, current)
          : undefined

  const rows: BrowserRow[] = subfolders.map((folder) => ({
    key: folder.id,
    name: folder.name,
    mark: folderMark,
    detail: folderSummary(folders, folder),
    fileCount: folder.fileCount.toLocaleString(),
    size: sizeLabel(folder.totalSize),
    updated: dateLabel(folder.lastFileAt),
    onOpen: () => onNavigate({ companyId, folderId: folder.id }),
    actions: canManage ? (
      <TableRowActions>
        <TableRowAction
          icon={Pencil}
          label={`Rename ${folder.name}`}
          onSelect={() => setRenaming(folder)}
        />
        {folder.isDefault ? null : (
          <TableRowAction
            icon={Trash2}
            label={`Delete ${folder.name}`}
            variant="destructive"
            onSelect={() => setDeleting(folder)}
          />
        )}
      </TableRowActions>
    ) : null,
  }))
  if (atCompanyTop && unfiledCount > 0) {
    rows.push({
      key: UNFILED,
      name: 'Unfiled',
      mark: <Inbox className={MARK_CLASS} aria-hidden />,
      detail: countLabel(unfiledCount, 'file'),
      fileCount: unfiledCount.toLocaleString(),
      size: NONE,
      updated: NONE,
      onOpen: () => onNavigate({ companyId, folderId: UNFILED }),
    })
  }
  if (atCompanyTop && employeeFileCount > 0) {
    rows.push({
      key: EMPLOYEE_FILES,
      name: 'Employee files',
      mark: <Users className={MARK_CLASS} aria-hidden />,
      detail: `${countLabel(employeeFileCount, 'file')} · Read-only`,
      fileCount: employeeFileCount.toLocaleString(),
      size: NONE,
      updated: NONE,
      onOpen: () => onNavigate({ companyId, folderId: EMPLOYEE_FILES }),
    })
  }
  if (current && current.id === recruitment?.id && candidateFileCount > 0) {
    rows.push({
      key: CANDIDATE_FILES,
      name: 'Candidate files',
      mark: <Contact className={MARK_CLASS} aria-hidden />,
      detail: `${countLabel(candidateFileCount, 'file')} · Read-only`,
      fileCount: candidateFileCount.toLocaleString(),
      size: NONE,
      updated: NONE,
      onOpen: () => onNavigate({ companyId, folderId: CANDIDATE_FILES }),
    })
  }

  return (
    <div
      className={cn(
        'min-w-0 space-y-5 rounded-lg',
        isDragging && 'outline-dashed outline-2 outline-offset-4 outline-primary',
      )}
      onDragEnter={(event) => {
        if (canUploadHere && event.dataTransfer.types.includes('Files')) setIsDragging(true)
      }}
      onDragOver={(event) => {
        if (canUploadHere) event.preventDefault()
      }}
      onDragLeave={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node)) setIsDragging(false)
      }}
      onDrop={(event) => {
        event.preventDefault()
        setIsDragging(false)
        if (canUploadHere && event.dataTransfer.files?.length) uploadFiles(event.dataTransfer.files)
      }}
    >
      <DriveHeader
        trail={trail}
        title={title}
        summary={summary}
        search={
          <WorkspaceToolbarSearch
            value={searchInput}
            onChange={setSearchInput}
            placeholder={
              isEmployeeFiles
                ? 'Search employee files'
                : isCandidateFiles
                  ? 'Search candidate files'
                  : `Search ${company.name} files`
            }
            ariaLabel="Search files"
            active={search !== ''}
          />
        }
        actions={
          canAddFolderHere || canUploadHere ? (
            <>
              {canAddFolderHere ? (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setIsCreating(true)}
                  className="h-9 gap-1.5 whitespace-nowrap"
                >
                  <FolderPlus className="h-4 w-4" aria-hidden />
                  New folder
                </Button>
              ) : null}
              {canUploadHere ? (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept={DOCUMENT_UPLOAD_ACCEPT}
                    className="sr-only"
                    aria-label={`Choose files for ${current?.name}`}
                    onChange={(event) => {
                      if (event.target.files?.length) uploadFiles(event.target.files)
                      event.target.value = ''
                    }}
                  />
                  <Button
                    type="button"
                    variant="primary"
                    loading={upload.isPending}
                    onClick={() => fileInputRef.current?.click()}
                    className="h-9 gap-1.5 whitespace-nowrap"
                  >
                    <Upload className="h-4 w-4" aria-hidden />
                    Upload
                  </Button>
                </>
              ) : null}
            </>
          ) : undefined
        }
      />

      {uploadError ? (
        <p
          className="rounded-lg border border-danger/30 bg-danger/10 px-3 py-1.5 text-sm text-danger"
          role="alert"
        >
          {uploadError}
        </p>
      ) : null}

      {isLoading ? (
        <SectionLoader variant="list" label="Loading folders" />
      ) : error ? (
        <QueryErrorState error={error} size="inline" title="Couldn't load folders" onRetry={refetch} />
      ) : search ? (
        <DriveFiles
          key={`search:${search}`}
          companyId={companyId}
          folderId={isReadOnlyView ? (folderId ?? undefined) : undefined}
          search={search}
          showTotal
          canManage={canManage && !isReadOnlyView}
          canDelete={canDelete && !isReadOnlyView}
          canMove={canManage && !isReadOnlyView}
          emptyTitle="No files match your search"
        />
      ) : (
        <div className="space-y-8">
          {rows.length > 0 ? (
            <BrowserTable label="Folders" nameHeading="Folder" rows={rows} showActions={canManage} />
          ) : null}
          {current || isFixedView ? (
            <DriveFiles
              key={folderId ?? ''}
              companyId={companyId}
              folderId={isFixedView ? (folderId ?? undefined) : current?.id}
              canManage={canManage && !isReadOnlyView}
              canDelete={canDelete && !isReadOnlyView}
              canMove={canManage && !isReadOnlyView}
              hideCategory={!isFixedView}
              emptyTitle={isReadOnlyView ? 'No files here' : 'No files in this folder'}
              emptyDescription={canUploadHere ? 'Upload files, or drop them here.' : undefined}
            />
          ) : null}
        </div>
      )}

      {isCreating ? (
        <DriveFolderDialog
          title={current ? `New folder in ${current.name}` : `New folder in ${company.name}`}
          submitLabel="Create folder"
          isPending={createFolder.isPending}
          onClose={() => setIsCreating(false)}
          onSubmit={(name) =>
            createFolder.mutate(
              { name, parentId: current?.id },
              { onSuccess: () => setIsCreating(false) },
            )
          }
        />
      ) : null}
      {renaming ? (
        <DriveFolderDialog
          title={`Rename ${renaming.name}`}
          submitLabel="Rename"
          initialName={renaming.name}
          isPending={renameFolder.isPending}
          onClose={() => setRenaming(null)}
          onSubmit={(name) =>
            renameFolder.mutate(
              { id: renaming.id, name },
              { onSuccess: () => setRenaming(null) },
            )
          }
        />
      ) : null}
      <ConfirmDialog
        open={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={() => {
          if (!deleting) return
          deleteFolder.mutate(deleting.id, { onSettled: () => setDeleting(null) })
        }}
        title="Delete folder"
        message={`Delete ${deleting?.name ?? 'this folder'}? Only empty folders can be deleted.`}
        confirmLabel="Delete"
        variant="danger"
        confirmLoading={deleteFolder.isPending}
      />
    </div>
  )
}

function CompanyMark({ company }: { company: DriveCompany }): React.ReactElement {
  const color = getCompanyColor(company._id, company.color)
  return (
    <span
      className={cn('h-2.5 w-2.5 rounded-full', company.color ? '' : color.dotBg)}
      style={company.color ? { backgroundColor: color.dot } : undefined}
      aria-hidden
    />
  )
}

/** The top level: every company, with what each one holds. */
function DriveCompanyList({
  companies,
  onNavigate,
}: {
  companies: DriveCompany[]
  onNavigate: (location: DriveLocation) => void
}): React.ReactElement {
  const { data: summaries } = useDriveCompanySummaries()
  const [search, setSearch] = useState('')

  const summaryByCompany = useMemo(
    () => new Map((summaries ?? []).map((summary) => [summary.companyId, summary])),
    [summaries],
  )
  const query = search.trim().toLocaleLowerCase()
  const visible = companies.filter((company) => company.name.toLocaleLowerCase().includes(query))
  const fileTotal = (summaries ?? []).reduce((total, summary) => total + summary.fileCount, 0)

  const rows: BrowserRow[] = visible.map((company) => {
    const summary = summaryByCompany.get(company._id)
    return {
      key: company._id,
      name: company.name,
      mark: <CompanyMark company={company} />,
      detail: summary ? countLabel(summary.fileCount, 'file') : summaries ? 'No files yet' : NONE,
      // Before the totals load, or if they fail, a blank beats a false zero.
      fileCount: summaries ? (summary?.fileCount ?? 0).toLocaleString() : NONE,
      size: sizeLabel(summary?.totalSize ?? 0),
      updated: dateLabel(summary?.lastFileAt),
      onOpen: () => onNavigate({ companyId: company._id, folderId: null }),
    }
  })

  return (
    <div className="min-w-0 space-y-5">
      <DriveHeader
        trail={[]}
        title="All companies"
        summary={
          summaries
            ? `${companies.length.toLocaleString()} ${companies.length === 1 ? 'company' : 'companies'} · ${countLabel(fileTotal, 'file')}`
            : undefined
        }
        search={
          <WorkspaceToolbarSearch
            value={search}
            onChange={setSearch}
            placeholder="Find a company"
            ariaLabel="Find a company"
            active={query !== ''}
          />
        }
      />
      {rows.length > 0 ? (
        <BrowserTable label="Companies" nameHeading="Company" rows={rows} showActions={false} />
      ) : (
        <EmptyState icon={HardDrive} title="No company matches your search" />
      )}
    </div>
  )
}

export interface CompanyDriveProps {
  companies: DriveCompany[]
  canManage: boolean
  canDelete: boolean
}

/**
 * Every company's files, in folders, browsed in one place: open a company, then a
 * folder, and step back through the trail above the heading. Where you are lives
 * in the URL, so a folder can be linked and the browser's back button works.
 */
export function CompanyDrive({
  companies,
  canManage,
  canDelete,
}: CompanyDriveProps): React.ReactElement {
  const [searchParams, setSearchParams] = useSearchParams()
  const requestedCompanyId = searchParams.get('companyId')
  const onlyCompany = companies.length === 1 ? companies[0] : null
  const company =
    companies.find((candidate) => candidate._id === requestedCompanyId) ?? onlyCompany
  const folderId = company ? searchParams.get('folderId') : null

  const navigate = (next: DriveLocation | null): void => {
    setSearchParams((previous) => {
      const params = new URLSearchParams(previous)
      params.delete('companyId')
      params.delete('folderId')
      if (next) params.set('companyId', next.companyId)
      if (next?.folderId) params.set('folderId', next.folderId)
      return params
    })
  }

  if (companies.length === 0) {
    return (
      <EmptyState
        icon={HardDrive}
        title="No companies yet"
        description="Files live in a company's Drive."
      />
    )
  }

  if (!company) return <DriveCompanyList companies={companies} onNavigate={navigate} />

  return (
    <DriveCompanyView
      key={company._id}
      company={company}
      folderId={folderId}
      rootCrumb={
        onlyCompany ? undefined : { label: 'All companies', onSelect: () => navigate(null) }
      }
      canManage={canManage}
      canDelete={canDelete}
      onNavigate={navigate}
    />
  )
}

CompanyDrive.displayName = 'CompanyDrive'
